import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { RewardPoints } from '@/models/loyalty';
import { CustomerProfile, OfferTypeFilter, Reward } from '@/models';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';

@Component({
  name: 'CmgFloatingRewardTracker',
})
export default class CmgFloatingRewardTracker extends Vue {
  @Prop({ type: Boolean, required: false, default: true })
  shouldFloat?: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  shouldShowView?: boolean;

  get servicesOnline(): boolean {
    return !this.$appStore.state.reward.unreachable;
  }

  get isMobile(): boolean {
    return this.$services.ui.isMobile();
  }

  get medallionImageUrl(): string {
    if (this.favoritedReward) {
      switch (this.favoritedReward.offerType) {
        case OfferTypeFilter.Food:
          return this.$appStore.state.cms.images.logos.medallionFood.default;
        
        case OfferTypeFilter.Goods:
          return this.$appStore.state.cms.images.logos.medallionGoods.default;
        
        case OfferTypeFilter.Giving:
          return this.$appStore.state.cms.images.logos.medallionGiving.default;
      }
    }

    return '';
  }

  get isLimitedTimeOffer(): boolean {
    return !!this.favoritedReward?.endDate ?? false;
  }

  get daysLeftToRedeem(): number {
    if (this.isLimitedTimeOffer) {
      const today = new Date().getTime();
      const endDate = new Date(this.favoritedReward!.endDate).getTime();
      const milliseconds = 1000;
      const seconds = 60;
      const minutes = 60;
      const hours = 24;
      const oneDay = milliseconds * seconds * minutes * hours;

      return Math.round((endDate - today) / oneDay);
    }

    return 0;
  }

  get message(): string {
    if (this.progressPercentage < 100) {
      return this.isLimitedTimeOffer
        ? `${ this.daysLeftToRedeem } day${ this.daysLeftToRedeem > 1 ? 's' : '' } to complete, ${ this.pointsNeeded } points to go`
        : `${ this.pointsNeeded } points to unlock your favorite reward`;
    }

    return 'You unlocked your favorite reward';
  }

  get points(): RewardPoints | null {
    return this.$appStore.state.customer.rewardPoints;
  }

  get pointsBalance(): number {
    return this.$services.reward.getPointsBalance();
  }

  get pointsNeeded(): number {
    if (this.favoritedReward && this.favoritedReward.points > this.pointsBalance) {
        return this.favoritedReward.points - this.pointsBalance;
    }

    return 0;
  }

  get progressPercentage(): number {
    return this.$services.reward.getProgressPercentage();
  }

  get favoritedReward(): Reward | undefined {
    return this.$services.reward.getFavoriteReward();
  }

  get isEnrolledInLoyalty(): boolean {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  onClickTracker() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: this.favoritedReward!.title + AnalyticsEventAction.FloatingTrackerDetailsClick,
      label: AnalyticsEventLabel.RewardStoreSpecific + this.favoritedReward!.title,
    });
    if (this.isMobile) {
      this.showRewardDetailModal();
    }
  }
  
  onClickRewardTitle() {
    this.showRewardDetailModal();
  }
  
  showRewardDetailModal() {
    this.$services.googleAnalytics.sendCustomEvent({
      category: AnalyticsEventCategory.RewardsLanding,
      action: this.favoritedReward!.title + AnalyticsEventAction.FavRewardDetailsView,
      label: AnalyticsEventLabel.FavRewardDetailsViewed + this.favoritedReward!.title,
    });
    this.$services.ui.setModalVisibility('rewardDetail', true, { reward: this.favoritedReward! });
  }
}
